<template>
  <div class="table-qr-container">
    <!-- Loading State -->
    <div v-if="isLoading" class="loading-state">
      <div class="sk-chase">
        <div class="sk-chase-dot"></div>
        <div class="sk-chase-dot"></div>
        <div class="sk-chase-dot"></div>
        <div class="sk-chase-dot"></div>
        <div class="sk-chase-dot"></div>
        <div class="sk-chase-dot"></div>
      </div>
      <p class="loading-text">Finding your tasting...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="showError" class="error-state">
      <p class="error-text">Unable to connect. Please check your internet connection.</p>
      <button class="btn btn-primary" @click="retryLookup">Try Again</button>
    </div>

    <!-- Test Mode (no tasting assigned) -->
    <div v-else-if="showTestMode" class="display-info">
      <div class="info-row">
        <span class="label">SET:</span>
        <span class="value">{{ set }}</span>
      </div>
      <div class="info-row">
        <span class="label">TABLE:</span>
        <span class="value">{{ table }}</span>
      </div>
      <p class="test-note">No tasting assigned to this set today</p>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';

export default {
  name: 'TableQR',
  props: ['set', 'table'],

  data() {
    return {
      isLoading: true,
      showError: false,
      showTestMode: false,
    };
  },

  methods: {
    ...mapActions(['fetchTastings', 'setCurrentTasting']),

    async retryLookup() {
      this.showError = false;
      this.isLoading = true;
      await this.lookupTasting();
    },

    async lookupTasting() {
      try {
        // 1. Call the table-cards API for today's date
        const response = await fetch('https://www.skurnik.com/api/tastings/table-cards.php');
        const data = await response.json();

        if (!data.success) {
          this.showTestMode = true;
          this.isLoading = false;
          return;
        }

        // 2. Check if this set has a tasting assigned
        const setLetter = this.set ? this.set.toUpperCase() : '';
        const tastingId = data.mappings[setLetter];

        if (!tastingId) {
          // No tasting for this set today - show test page
          this.showTestMode = true;
          this.isLoading = false;
          return;
        }

        // 3. Check if we're already on this tasting
        const currentTastingId = localStorage.getItem('tasting_id');
        if (currentTastingId && currentTastingId == tastingId) {
          // Same tasting - just scroll to the table
          localStorage.setItem('qr_scroll_to_table', this.table);
          this.$router.push({ name: 'Browse' });
          return;
        }

        // 4. Different tasting - set it up
        await this.setupTastingAndNavigate(tastingId, this.table);

      } catch (error) {
        console.error('QR routing error:', error);
        this.showError = true;
        this.isLoading = false;
      }
    },

    async setupTastingAndNavigate(tastingId, tableNumber) {
      // 1. Fetch all tastings to get the tasting details
      const tastingsResponse = await this.fetchTastings({});

      // Find the matching tasting (response may be array or object)
      let tasting = null;
      for (const idx in tastingsResponse) {
        if (tastingsResponse[idx].tasting_id == tastingId) {
          tasting = tastingsResponse[idx];
          break;
        }
      }

      if (!tasting) {
        this.showTestMode = true;
        this.isLoading = false;
        return;
      }

      // 2. Set current tasting (this clears old cached data)
      await this.setCurrentTasting(tasting);
      localStorage.setItem('tasting_id', tastingId);

      // 3. Determine and set the appropriate state for pricing
      const stateToUse = this.determineState(tasting);
      if (stateToUse) {
        localStorage.setItem('chosen_state', stateToUse);
      }

      // 4. Store table to scroll to
      localStorage.setItem('qr_scroll_to_table', tableNumber);

      // 5. Navigate to Browse
      this.$router.push({ name: 'Browse' });
    },

    determineState(tasting) {
      // Priority 1: Logged-in user's profile state
      const userData = localStorage.getItem('user');
      if (userData) {
        try {
          const user = JSON.parse(userData);
          if (user.state) {
            return user.state;
          }
        } catch (e) {
          // Invalid JSON, continue to next priority
        }
      }

      // Priority 2: Previously chosen state (if valid for this tasting)
      const chosenState = localStorage.getItem('chosen_state');
      if (chosenState && tasting.states && tasting.states.includes(chosenState)) {
        return chosenState;
      }

      // Priority 3: Tasting's default state (first in array)
      if (tasting.states && tasting.states.length > 0) {
        return tasting.states[0];
      }

      return null;
    },
  },

  created() {
    this.lookupTasting();
  },
};
</script>

<style scoped>
.table-qr-container {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background-color: #f5f5f5;
}

.loading-state {
  text-align: center;
}

.loading-text {
  margin-top: 1.5rem;
  font-size: 1.2rem;
  color: #666;
}

.error-state {
  text-align: center;
  padding: 2rem;
}

.error-text {
  font-size: 1.2rem;
  color: #dc3545;
  margin-bottom: 1.5rem;
}

.display-info {
  text-align: center;
  padding: 2rem;
}

.info-row {
  margin: 2rem 0;
}

.label {
  display: block;
  font-size: 3rem;
  font-weight: bold;
  color: #333;
  margin-bottom: 1rem;
}

.value {
  display: block;
  font-size: 6rem;
  font-weight: bold;
  color: #000;
}

.test-note {
  margin-top: 2rem;
  font-size: 1rem;
  color: #666;
  font-style: italic;
}

/* Loading spinner - matches existing app style */
.sk-chase {
  width: 40px;
  height: 40px;
  position: relative;
  animation: sk-chase 2.5s infinite linear both;
  margin: 0 auto;
}

.sk-chase-dot {
  width: 100%;
  height: 100%;
  position: absolute;
  left: 0;
  top: 0;
  animation: sk-chase-dot 2.0s infinite ease-in-out both;
}

.sk-chase-dot:before {
  content: '';
  display: block;
  width: 25%;
  height: 25%;
  background-color: #333;
  border-radius: 100%;
  animation: sk-chase-dot-before 2.0s infinite ease-in-out both;
}

.sk-chase-dot:nth-child(1) { animation-delay: -1.1s; }
.sk-chase-dot:nth-child(2) { animation-delay: -1.0s; }
.sk-chase-dot:nth-child(3) { animation-delay: -0.9s; }
.sk-chase-dot:nth-child(4) { animation-delay: -0.8s; }
.sk-chase-dot:nth-child(5) { animation-delay: -0.7s; }
.sk-chase-dot:nth-child(6) { animation-delay: -0.6s; }
.sk-chase-dot:nth-child(1):before { animation-delay: -1.1s; }
.sk-chase-dot:nth-child(2):before { animation-delay: -1.0s; }
.sk-chase-dot:nth-child(3):before { animation-delay: -0.9s; }
.sk-chase-dot:nth-child(4):before { animation-delay: -0.8s; }
.sk-chase-dot:nth-child(5):before { animation-delay: -0.7s; }
.sk-chase-dot:nth-child(6):before { animation-delay: -0.6s; }

@keyframes sk-chase {
  100% { transform: rotate(360deg); }
}

@keyframes sk-chase-dot {
  80%, 100% { transform: rotate(360deg); }
}

@keyframes sk-chase-dot-before {
  50% { transform: scale(0.4); }
  100%, 0% { transform: scale(1.0); }
}
</style>
